import { Component } from '@theme/component';
import { getIntersectionRoot } from '@theme/scroll-container';

/** SlackYard disclosure, video and sticky controls. Horizon owns all cart transport. */
class GbYardPdp extends Component {
  #listeners;
  #observers = [];
  #mutations;
  #timers = new Set();
  #mobile = window.matchMedia('(max-width: 749px)');
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #formVisible = true;
  #footerVisible = new Set();
  #returnFocus;
  #generation = 0;

  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() { super.disconnectedCallback(); this.#cleanup(); }

  #cleanup() {
    this.#generation++;
    this.#listeners?.abort();
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
    this.#mutations?.disconnect();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    for (const video of this.querySelectorAll('video')) video.pause();
    for (const dialog of this.querySelectorAll('dialog[open]')) dialog.close();
    this.#returnFocus = undefined;
  }

  #main() { return this.querySelector('[data-gb-main-atc]'); }

  #initialize() {
    this.#cleanup();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('click', this.#click, options);
    this.addEventListener('change', this.#change, options);
    window.addEventListener('resize', this.#observe, options);
    this.#motion.addEventListener('change', this.#observe, options);
    this.#mobile.addEventListener('change', () => {
      for (const fit of this.querySelectorAll('[data-gb-yard-fit]')) this.#step(fit, fit.dataset.complete === 'true' ? fit.querySelectorAll('[data-gb-yard-q]').length + 1 : 1, false);
    }, options);
    for (const fit of this.querySelectorAll('[data-gb-yard-fit]')) {
      fit.classList.add('is-enhanced');
      this.#syncFit(fit);
    }
    for (const gallery of this.querySelectorAll('.gb-yard-gallery')) {
      const trigger = gallery.querySelector('[data-gb-yard-open]');
      const frame = gallery.querySelector('.gb-pdp-gallery__frame');
      if (trigger && frame && trigger.parentElement !== frame) frame.append(trigger);
      const button = trigger?.querySelector('button');
      if (button) {
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', trigger.dataset.gbYardOpen);
      }
    }
    for (const dialog of this.querySelectorAll('.gb-yard-dialog')) {
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, options);
      dialog.addEventListener('close', () => {
        for (const video of dialog.querySelectorAll('video')) video.pause();
        if (this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
        this.#returnFocus = undefined;
      }, options);
    }
    this.#mutations = new MutationObserver(this.#paintSticky);
    const buy = this.querySelector('.gb-pdp-buy');
    if (buy) this.#mutations.observe(buy, { subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-busy'], childList: true, characterData: true });
    const price = this.querySelector('product-price');
    if (price) this.#mutations.observe(price, { subtree: true, attributes: true, attributeFilter: ['hidden'], childList: true, characterData: true });
    this.#observe();
  }

  #observe = () => {
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
    this.#footerVisible.clear();
    const root = getIntersectionRoot();
    const form = this.querySelector('product-form-component');
    if (form && this.dataset.sticky === 'true') {
      const observer = new IntersectionObserver(entries => {
        this.#formVisible = entries[0].isIntersecting;
        this.#paintSticky();
      }, { root });
      observer.observe(form);
      this.#observers.push(observer);
      const footers = [...document.querySelectorAll('footer, .site-footer, .shopify-section-group-footer-group')].filter(node => !this.contains(node));
      const footerObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#footerVisible.add(entry.target);
          else this.#footerVisible.delete(entry.target);
        }
        this.#paintSticky();
      }, { root });
      for (const footer of footers) footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }
    const videos = this.querySelectorAll('[data-gb-yard-inview] video');
    for (const video of videos) {
      video.pause();
      video.controls = this.#motion.matches;
      if (!this.#motion.matches) { video.setAttribute('aria-hidden', 'true'); video.tabIndex = -1; }
    }
    if (!this.#motion.matches) {
      const generation = this.#generation;
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) continue;
          video.dataset.gbYardVisible = String(entry.isIntersecting);
          if (entry.isIntersecting) {
            if (video.dataset.src && !video.getAttribute('src')) video.src = video.dataset.src;
            video.muted = true;
            video.loop = true;
            video.play()?.then(() => {
              if (generation !== this.#generation || !this.isConnected || this.#motion.matches || video.dataset.gbYardVisible !== 'true') video.pause();
            }).catch(() => { video.controls = true; video.removeAttribute('aria-hidden'); video.removeAttribute('tabindex'); });
          } else video.pause();
        }
      }, { root, threshold: .35 });
      for (const video of videos) observer.observe(video);
      this.#observers.push(observer);
    } else {
      for (const video of videos) {
        if (video.dataset.src && !video.getAttribute('src')) video.src = video.dataset.src;
        video.removeAttribute('aria-hidden');
        video.removeAttribute('tabindex');
      }
    }
    this.#paintSticky();
  };

  #paintSticky = () => {
    const sticky = this.querySelector('[data-gb-yard-sticky]');
    const main = this.#main();
    if (!sticky || !(main instanceof HTMLButtonElement)) return;
    const show = !this.#formVisible && this.#footerVisible.size === 0;
    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', String(!show));
    sticky.inert = !show;
    const button = sticky.querySelector('button');
    if (button) {
      button.disabled = main.disabled;
      button.setAttribute('aria-busy', main.getAttribute('aria-busy') || 'false');
      const label = button.querySelector('.gb-btn__label');
      const mainLabel = main.querySelector('.gb-btn__label');
      if (label && !label.dataset.gbYardLabel) label.dataset.gbYardLabel = label.textContent;
      if (label && main.disabled && mainLabel) label.textContent = mainLabel.textContent;
      else if (label) label.textContent = label.dataset.gbYardLabel || label.textContent;
    }
    const price = sticky.querySelector('[data-gb-yard-sticky-price]');
    const source = this.querySelector('.gb-pdp-price__row');
    if (price && source) {
      const values = [...source.querySelectorAll('.gb-pdp-price__compare, .gb-pdp-price__sale')].filter(node => !node.closest('[hidden]'));
      price.replaceChildren(...values.map(node => {
        const copy = document.createElement(node.tagName === 'S' ? 's' : 'span');
        copy.textContent = node.textContent;
        return copy;
      }));
    }
  };

  #syncFit(fit) {
    const questions = [...fit.querySelectorAll('[data-gb-yard-q]')];
    let yes = 0, no = 0;
    for (const question of questions) {
      const chosen = question.querySelector('input:checked');
      const isNo = chosen?.value === 'no';
      question.classList.toggle('is-no', isNo);
      const tip = question.querySelector('[data-gb-yard-tip]');
      if (tip) tip.hidden = !isNo;
      if (chosen?.value === 'yes') yes++;
      if (isNo) no++;
    }
    const complete = yes + no === questions.length;
    fit.dataset.complete = String(complete);
    const caption = fit.querySelector('[data-gb-yard-fit-caption]');
    if (caption) caption.textContent = complete ? (no > 0 ? fit.dataset.recommendationsCaption : fit.dataset.doneCaption) : fit.dataset.defaultCaption;
    const finish = fit.querySelector('[data-gb-yard-fit-finish]');
    if (finish instanceof HTMLButtonElement) finish.hidden = !complete;
    const ok = fit.querySelector('[data-gb-yard-ok]');
    const mixed = fit.querySelector('[data-gb-yard-mixed]');
    if (ok) ok.hidden = yes !== questions.length;
    if (mixed) mixed.hidden = no === 0;
  }

  #step(fit, number, focus = true) {
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    const body = fit.querySelector('[data-gb-yard-fit-body]');
    if (!body) return;
    const total = fit.querySelectorAll('[data-gb-yard-q]').length;
    body.dataset.step = number > total ? 'done' : String(number);
    const count = fit.querySelector('[data-gb-yard-fit-n]');
    if (count && number <= total) count.textContent = String(number);
    if (!focus) return;
    const finished = number > total && fit.dataset.complete === 'true';
    if (finished) fit.open = false;
    const target = finished ? fit.querySelector('summary') : fit.querySelector(`[data-gb-yard-q="${number}"] input`);
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  #change = event => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const question = event.target.closest('[data-gb-yard-q]');
    const fit = event.target.closest('[data-gb-yard-fit]');
    if (!question || !fit) return;
    this.#syncFit(fit);
    // A short delay lets the checked state paint before the phone advances.
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    if (event.target.value === 'yes' && this.#mobile.matches) {
      const timer = setTimeout(() => { this.#timers.delete(timer); this.#step(fit, Number(question.dataset.gbYardQ) + 1); }, 260);
      this.#timers.add(timer);
    }
  };

  #click = event => {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('[data-gb-yard-open]');
    if (open) {
      const dialog = document.getElementById(open.dataset.gbYardOpen);
      if (!(dialog instanceof HTMLDialogElement)) return;
      this.#returnFocus = open.querySelector('button');
      dialog.showModal();
      dialog.querySelector('video')?.play()?.catch(() => {});
      return;
    }
    const close = event.target.closest('[data-gb-yard-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const finish = event.target.closest('[data-gb-yard-fit-finish]');
    if (finish) {
      const fit = finish.closest('[data-gb-yard-fit]');
      if (fit) this.#step(fit, fit.querySelectorAll('[data-gb-yard-q]').length + 1);
      return;
    }
    const next = event.target.closest('[data-gb-yard-fit-next]');
    if (next) {
      const fit = next.closest('[data-gb-yard-fit]');
      const question = next.closest('[data-gb-yard-q]');
      if (fit && question) this.#step(fit, Number(question.dataset.gbYardQ) + 1);
      return;
    }
    const restart = event.target.closest('[data-gb-yard-fit-restart]');
    if (restart) {
      const fit = restart.closest('[data-gb-yard-fit]');
      if (!fit) return;
      for (const input of fit.querySelectorAll('input:checked')) input.checked = false;
      this.#syncFit(fit);
      this.#step(fit, 1);
      return;
    }
    if (event.target.closest('[data-gb-yard-add]')) {
      const main = this.#main();
      if (main instanceof HTMLButtonElement && !main.disabled) main.click();
    }
  };
}
if (!customElements.get('gb-yard-pdp')) customElements.define('gb-yard-pdp', GbYardPdp);
